import _ from 'lodash'
import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type Employee from 'modules/employees/store/models/Employee'
import moment from 'moment'

const sortContractVersionsByEffectiveOn = (models: ContractVersion[]) => {
  return _.sortBy(models, (cv: ContractVersion) => cv.effectiveOn)
}

// Returns the subset of `models` (which should be the contract versions for
// an employee) that were effective (aka "current") between `startDate` and
// `endDate`.
const filterContractVersionsByCurrentWithinDateRange = (
  models: ContractVersion[],
  startDate: moment.Moment,
  endDate: moment.Moment
): ContractVersion[] => {
  const sortedModels = sortContractVersionsByEffectiveOn(models)

  return sortedModels.filter(
    (model, index) =>
      model.effectiveOn.isSameOrBefore(endDate, 'day') &&
      (index === sortedModels.length - 1 || // last one?
        sortedModels[index + 1]?.effectiveOn.isAfter(startDate, 'day'))
  )
}

// Returns the subset of `models` (which should be the contract versions for
// an employee) that were effective on the specified `date`. This subset will
// contain 1 contract version at the most.
const filterContractVersionsByCurrent = (
  models: ContractVersion[],
  date: moment.Moment
): ContractVersion[] => {
  return filterContractVersionsByCurrentWithinDateRange(models, date, date)
}

const getUpcomingContractVersions = (
  models: ContractVersion[],
  date: moment.Moment
): ContractVersion | null | undefined => {
  const future = models.filter((cv) => cv.effectiveOn.isAfter(date))

  return sortContractVersionsByEffectiveOn(future)[0]
}

const referenceContract = (
  models: ContractVersion[],
  date: moment.Moment
): ContractVersion | null | undefined => {
  const currentVersions = filterContractVersionsByCurrent(models, date)
  const currentNotTerminated = currentVersions.filter((m) => !m.terminatedOn(date))[0]

  if (currentNotTerminated) return currentNotTerminated

  const upcoming = getUpcomingContractVersions(models, date)

  if (upcoming) return upcoming

  return currentVersions[0]
}

const employeeContractVersions = (contractVersions: ContractVersions, employee: Employee) => {
  return contractVersions.filter({
    employee_id: employee.attributes.id,
  })
}

export const employeeReferenceContract = (
  contractVersions: ContractVersions,
  employee: Employee
) => {
  return referenceContract(employeeContractVersions(contractVersions, employee), moment())
}

// TODO: double check `getFilterValue` in permissions
export const employeeJobTitle = (contractVersions: ContractVersions, employee: Employee) => {
  return employeeReferenceContract(contractVersions, employee)?.attributes.job_title
}
